import { Form, Formik, getIn } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import ConfirmWithReason from '../../../components/confirm-with-reason';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadSaves, canWriteSaves, formatDateForDataGrid,
  formatDateForInput, getErrorObject, getMomentDate, getSavesEntityValue, isNullOrWhiteSpace
} from '../../../utils/helper';
import { } from '../../../utils/icons';
import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormHelperText, Grid, InputLabel, Select, TextField, Typography
} from '../../../utils/material-ui-core';

interface manageSavesCRUDProps {
  open: boolean;
  savesEntityKey: string;
  savesEntityValueKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

const formDefaultValue = {
  alignedValue: 0,
  avgValue: 0,
  effectiveDate: "",
  highValue: 0,
  lowValue: 0,
  note: "",
  region: "",
  country: "USA",
  savesTypeId: 0,
  expirationDate: "",
  expiryNote: "",
  expiredIn: "",
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  savesEntityValueKey: "",
  savesEntityName: "",
  savesEntityType: "",
  savesTypeName: "",
  active: true,
};

const formDefaultTouchValue = {
  alignedValue: false,
  avgValue: false,
  effectiveDate: false,
  highValue: false,
  lowValue: false,
  note: false,
  region: false,
  country: false,
  savesTypeId: false,
  expirationDate: false,
  expiryNote: false,
  lastModifiedBy: false,
  lastModifiedDateTime: false,
  savesEntityValueKey: false,
  savesEntityName: false,
  savesEntityType: false,
  savesTypeName: false,
  active: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  country: Yup.string().trim().required("Country is required."),
  region: Yup.string().trim().required("Region is required."),
  savesTypeId: Yup.number().required("Saves type is required.").moreThan(0, "Saves type is required."),
  alignedValue: Yup.number().nullable().moreThan(-1, "Aligned value can be empty or greater than or equal zero."),
  avgValue: Yup.number().nullable().moreThan(-1, "Average value can be empty or greater than or equal zero."),
  lowValue: Yup.number().nullable().moreThan(-1, "Low value can be empty or greater than or equal zero."),
  highValue: Yup.number().nullable().moreThan(-1, "High value can be empty or greater than or equal zero."),
  effectiveDate: Yup.date().required("Effective date is required.").min(getMomentDate('days', 0), "Effective date must be greater or equal to today."),
  expirationDate: Yup.date().nullable().notRequired().min(Yup.ref("effectiveDate"), "Expiration date can be empty or greater or equal to effective date."),
});

const ManageSavesCRUD = ({ open, savesEntityKey, savesEntityValueKey, onCallback }: manageSavesCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { userFunctionAccess, showAlert, referenceLookups } = useContext(AppContext);

  const [initialFormValues, setInitialFormValues] = useState<SavesEntityValue>(formDefaultValue);
  const [countries, setCountries] = useState<Country[]>([]);
  const [savesTypes, setSavesTypes] = useState<SavesType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [active, setActive] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [dataChanged, setDataChanged] = useState<boolean>(true);
  const [refreshList, setRefreshList] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [expiredConfirmDialogOpen, setExpiredConfirmDialogOpen] = useState<boolean>(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [inputError, setInputError] = useState<boolean>(false);

  const openSavesValueExpiredConfirmation = () => {
    if (!canWriteSaves(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }
    setExpiredConfirmDialogOpen(true);
  };

  // Cancel dialog
  const cancel = () => {
    if (refreshList) {
      onCallback({ status: "refresh", data: "" });
      return;
    }
    onCallback({ status: "cancel", data: "" });
  };

  const savesValueExpiredDialogConfirmation = async (confirmWithReasonOutput: ConfirmWithReasonOutput) => {
    setExpiredConfirmDialogOpen(false);

    if (confirmWithReasonOutput.confirm) {
      const payload = {
        note: confirmWithReasonOutput.reason
      }
      setRefreshList(true);

      try {
        setSaveInProgress(true);
        await API.post("/api/saves/entities/" + savesEntityKey + "/values/" + savesEntityValueKey + "/expire", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });

            if (savesEntityValueKey === referenceLookups.newRecordDefaultId) {
              savesEntityValueKey = response.data.savesEntityValueKey;

              if (savesEntityValueKey === null) {
                cancel();
                setSaveInProgress(false);
                return;
              }
            }

            setSaveInProgress(false);
            setLoading(true);
            await getSavesValue();
            setLoading(false);
            return;
          })
          .catch((error) => {
            setSaveInProgress(false);
            showAlert({
              message: apiReponseError(error, "Failed to expire saves entity value."),
              severity: "error",
            });
          });
      } catch (e) {
        setSaveInProgress(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Get client api key data
  const getSavesValue = async () => {
    const apiResponse = await getSavesEntityValue(savesEntityKey, savesEntityValueKey);
    if (apiResponse.success) {
      setCountries(apiResponse.data.countries);
      setSavesTypes(apiResponse.data.savesTypes);

      setInitialFormValues(apiResponse.data.savesEntityValue);

      setReadonly(!canWriteSaves(userFunctionAccess));

      setActive(true);

      if (!apiResponse.data.savesEntityValue.active) {
        setReadonly(true);
        setActive(false);
      }
    } else {
      setLoading(false);
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancel();
      return;
    }
  };

  const scrollToFieldError = (formRef: any) => {
    if (readonly) {
      return;
    }

    let returnValue = false;
    if (!formRef.current.isValid) {
      try {
        Object.keys(formRef.current.errors).forEach((key) => {
          let element = document.querySelector(`input[name='${key}']`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            returnValue = true;
            return;
          } else {
            element = document.querySelector(`select[name='${key}']`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              returnValue = true;
              return;
            }
          }
        });
      } catch (e) { }

      showAlert({
        message: "One or more validation errors occurred.",
        severity: "error",
      });
    }
    return returnValue;
  };

  // Save Form
  const submitForm = () => {
    if (!scrollToFieldError(formReference)) {
      formReference.current.handleSubmit();
    }
  };

  // Save values
  const save = async () => {
    setValidationErrorMessage("");
    setInputError(false);
    setRefreshList(true);

    const payload = {
      savesEntityValue: {
        country: formReference.current.values.country,
        region: formReference.current.values.region,
        savesTypeId: formReference.current.values.savesTypeId,
        alignedValue: formReference.current.values.alignedValue,
        avgValue: formReference.current.values.avgValue,
        lowValue: formReference.current.values.lowValue,
        highValue: formReference.current.values.highValue,
        effectiveDate: formReference.current.values.effectiveDate,
        expirationDate: formReference.current.values.expirationDate,
        note: formReference.current.values.note,
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/saves/entities/" + savesEntityKey + "/values/" + savesEntityValueKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });

          if (savesEntityValueKey === referenceLookups.newRecordDefaultId) {
            savesEntityValueKey = response.data.savesEntityValueKey;
          }
          setSaveInProgress(false);
          setLoading(false);
          cancel();
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "savesEntityValue.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
            scrollToFieldError(formReference);
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {

            setValidationErrorMessage(apiReponseError(error, ""));
            setInputError(true);

            showAlert({
              message: apiReponseError(error, "Failed to save saves entity value."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setValidationErrorMessage("");
        setInputError(false);
        setRefreshList(false);
        setReadonly(true);
        setDataChanged(false);
        setSaveInProgress(false);
        setIsNew(false);

        if (isNullOrWhiteSpace(savesEntityValueKey)) {
          showAlert({
            message: "Saves value key is missing.",
            severity: "error",
          });
          cancel();
          return;
        }

        if (!canReadSaves(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (savesEntityValueKey === referenceLookups.newRecordDefaultId) {

          if (!canWriteSaves(userFunctionAccess)) {
            showAlert({ message: "Access Denied.", severity: "error" });
            cancel();
            return;
          }

          setIsNew(true);
          setLoading(true);
          await getSavesValue();
          setLoading(false);
          setRefreshList(true);
        } else {

          if (!canReadSaves(userFunctionAccess)) {
            showAlert({ message: "Access Denied.", severity: "error" });
            cancel();
            return;
          }

          setIsNew(false);
          setLoading(true);
          await getSavesValue();
          setLoading(false);
        }
      }
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, savesEntityValueKey]);

  return (
    <>
      <ConfirmWithReason title="Confirmation" content={"<h3><b>Once this saves value is set to expired, it can't be undone.</b><h3/>"} yesText={"CONFIRM"} noText="CANCEL" open={expiredConfirmDialogOpen} showReason={true} required={true} reasonLabel={"Reason"} callbackOnConfirm={savesValueExpiredDialogConfirmation} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View " : isNew ? "New " : "Edit "} Saves Entity value
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              <Formik
                innerRef={formReference}
                initialValues={initialFormValues}
                validationSchema={formValidationSchema}
                validateOnMount={true}
                initialTouched={formDefaultTouchValue}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values: SavesEntityValue) => {
                  save();
                }}
              >
                {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, setFieldError, resetForm }) => (
                  <Form
                    onChange={() => {
                      setValidationErrorMessage("");
                      setInputError(false);
                    }}
                  >
                    <Grid container spacing={3}>
                      {inputError ? (
                        <Grid item xs={12}>
                          <Box p={1}></Box>
                          <Alert severity="error">
                            <AlertTitle>Immediate Action Required</AlertTitle>
                            {validationErrorMessage !== "" ? validationErrorMessage : "Validation failed. Required information is missing or not valid."}
                          </Alert>
                          <Box p={1}></Box>
                        </Grid>
                      ) : null}

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={true} label="Entity Name" value={values.savesEntityName} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={true} label="Entity Type" value={values.savesEntityType} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "country"))}>
                          <InputLabel id="country-label" shrink={true}>
                            Country
                          </InputLabel>
                          <Select
                            disabled={readonly || !isNew}
                            native
                            labelId="country-label"
                            id="country"
                            name="country"
                            value={values.country}
                            onChange={(e) => {
                              handleChange(e);

                              setTimeout(() => {
                                formReference.current.setFieldValue("region", "");
                                formReference.current.setFieldTouched("region", true);
                                formReference.current.validateForm();
                              }, 200);
                            }}
                            label="Country"
                            error={Boolean(getIn(errors, "country"))}
                          >
                            <option value={""}></option>
                            {countries.map((country, index) => (
                              <option value={country.code} key={index}>
                                {country.name}
                              </option>
                            ))}
                          </Select>
                          <FormHelperText error>{getIn(errors, "country")}</FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "region"))}>
                          <InputLabel id="region-label" shrink={true}>
                            Region
                          </InputLabel>
                          <Select disabled={readonly || !isNew} native labelId="region-label" id="region" name="region" value={values.region} onChange={handleChange} label="Region" error={Boolean(getIn(errors, "region"))}>
                            <option value={""}></option>
                            {countries
                              .filter((country) => country.code === values.country)
                              .map((country, index) =>
                                country.states.map((state, index) => (
                                  <option value={state.code} key={index}>
                                    {state.name}
                                  </option>
                                ))
                              )}
                          </Select>
                          <FormHelperText error>{getIn(errors, "region")}</FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "savesTypeId"))}>
                          <InputLabel id="savesTypeId-label" shrink={true}>
                            Saves Type
                          </InputLabel>
                          <Select disabled={readonly || !isNew} native labelId="savesTypeId-label" id="savesTypeId" name="savesTypeId" value={values.savesTypeId} onChange={handleChange} label="Saves Type" error={Boolean(getIn(touched, "savesTypeId") && getIn(errors, "savesTypeId"))}>
                            <option value={0}></option>
                            {savesTypes.map((savesType, index) => (
                              <option value={savesType.id} key={index}>
                                {savesType.name}
                              </option>
                            ))}
                          </Select>
                          <FormHelperText error>{getIn(errors, "savesTypeId")}</FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} type='date' id="effectiveDate" label="Effective Date" defaultValue={formatDateForInput(values.effectiveDate)} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.effectiveDate)} helperText={errors.effectiveDate && touched.effectiveDate ? errors.effectiveDate : null} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} type='date' id="expirationDate" label="Expiration Date" defaultValue={formatDateForInput(values.expirationDate)} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.expirationDate)} helperText={errors.expirationDate && touched.expirationDate ? errors.expirationDate : null} />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField disabled={readonly} id="note" label="Note" value={values.note} multiline onChange={handleChange} onBlur={handleBlur} />
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={globalStyles.topic}>Values</Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} id="alignedValue" label="Aligned Value" value={values.alignedValue} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.alignedValue && Boolean(errors.alignedValue)} helperText={errors.alignedValue && touched.alignedValue ? errors.alignedValue : null} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} id="avgValue" label="Average Value" value={values.avgValue} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.avgValue && Boolean(errors.avgValue)} helperText={errors.avgValue && touched.avgValue ? errors.avgValue : null} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} id="lowValue" label="Low Value" value={values.lowValue} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.lowValue && Boolean(errors.lowValue)} helperText={errors.lowValue && touched.lowValue ? errors.lowValue : null} />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField disabled={readonly} id="highValue" label="High Value" value={values.highValue} type="number" onChange={handleChange} onBlur={handleBlur} error={touched.highValue && Boolean(errors.highValue)} helperText={errors.highValue && touched.highValue ? errors.highValue : null} />
                      </Grid>
                    </Grid>

                    {!isNew ? (
                      <>
                        <Box p={1}>&nbsp;</Box>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Audit</Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={6}>
                            <TextField disabled={true} label="Last Modified By" value={values.lastModifiedBy} />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6} lg={6}>
                            <TextField disabled={true} label="Last Modified On" value={formatDateForDataGrid(values.lastModifiedDateTime)} />
                          </Grid>
                        </Grid>
                        <Box p={2}>&nbsp;</Box>
                      </>
                    ) : (
                      <>
                        <Box p={2}>&nbsp;</Box>
                      </>
                    )}
                  </Form>
                )}
              </Formik>
            </DialogContent>
            <DialogActions>
              {!dataChanged && !isNew && active && canWriteSaves(userFunctionAccess) ? (
                <Button onClick={() => openSavesValueExpiredConfirmation()} variant="contained" className={pageStyles.expiredButton}>
                  Set Expired
                </Button>
              ) : null}

              {!readonly ? (
                <Button onClick={submitForm} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Create" : "Update"}
                </Button>
              ) : null}

              <Button onClick={() => cancel()} color="secondary" autoFocus className={globalStyles.buttonDefault}>
                <u>Close</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    expiredButton: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      marginLeft: "4px",
      marginRight: "4px",
      minWidth: "55px",
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
      },
    },
  })
);

export default ManageSavesCRUD;
